/* SEPA-Einzugslauf (v7 M16, 01.10.2026): aus den in Lexware festgeschriebenen Rechnungen
   entsteht eine pain.008-Datei (wie Sticky), die der Inhaber im Onlinebanking hochlädt.
   Erst der Klick „Bei der Bank eingereicht“ macht den Lauf verbindlich. */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Landmark, Download, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import api from '@/lib/api';
import BezahltPruefen from '@/components/BezahltPruefen';

const euro = (v) => (v ?? 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
const datum = (iso) => iso ? new Date(`${iso}T12:00`).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
const STATUS = {
  entwurf: ['Entwurf', 'bg-muted text-foreground'], datei_erzeugt: ['Datei erzeugt — bei der Bank hochladen', 'bg-amber-100 text-amber-700'],
  eingereicht: ['eingereicht', 'bg-emerald-100 text-emerald-700'], verworfen: ['verworfen', 'bg-muted text-muted-foreground line-through'],
};

export default function EinzugLauf({ monat }) {
  const [d, setD] = useState(null);
  const [vorschau, setVorschau] = useState(null);
  const [tag, setTag] = useState('');
  const [busy, setBusy] = useState('');
  const [frage, setFrage] = useState(null);      // { art: 'anlegen'|'einreichen'|'verwerfen', lauf }
  const [offenAlle, setOffenAlle] = useState({});
  const [schluessel, setSchluessel] = useState(null);   // Feldverschluesselung: Altbestand am JWT-Schluessel?
  const [nurCrm, setNurCrm] = useState(false);          // Uebergang: Sticky-Mandate auslassen

  const laden = useCallback(async () => {
    try {
      const r = (await api.get('/einzug/laeufe', { params: { monat } })).data;
      setD(r);
      setTag(t => t || r.fruehestens);
      api.get('/mandate/schluessel').then(x => setSchluessel(x.data)).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || 'Einzug nicht geladen'); }
  }, [monat]);
  const umschluesseln = async () => {
    setBusy('schluessel');
    try { const r = (await api.post('/mandate/schluessel/umschluesseln')).data; toast.success(`${r.umgeschrieben} Felder auf den neuen Schlüssel umgeschrieben`); setSchluessel(r); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Umschlüsseln fehlgeschlagen'); }
    setBusy('');
  };
  useEffect(() => { laden(); setVorschau(null); }, [laden]);

  const offen = d?.laeufe?.find(l => l.status === 'entwurf' || l.status === 'datei_erzeugt');
  const vorschauLaden = async () => {
    setBusy('vorschau');
    try { setVorschau((await api.get('/einzug/vorschau', { params: { monat, einzugstag: tag || undefined, nur_crm_mandate: nurCrm } })).data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Vorschau fehlgeschlagen'); }
    setBusy('');
  };
  const anlegen = async () => {
    setFrage(null); setBusy('anlegen');
    try {
      const r = (await api.post('/einzug/laeufe', { monat, einzugstag: tag || null, nur_crm_mandate: nurCrm })).data;
      toast.success(`Lauf angelegt: ${r.anzahl} Lastschriften, ${euro(r.summe)}`);
      setVorschau(null); laden();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Lauf nicht angelegt'); }
    setBusy('');
  };
  const herunterladen = async (lauf) => {
    setBusy(lauf.id);
    try {
      const r = await api.get(`/einzug/laeufe/${lauf.id}/datei`, { responseType: 'blob' });
      const name = (r.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || `SEPA_${lauf.einzugstag}.xml`;
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
      laden();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Datei nicht erzeugt'); }
    setBusy('');
  };
  const aktion = async (lauf, pfad, ok) => {
    setFrage(null); setBusy(lauf.id);
    try { await api.post(`/einzug/laeufe/${lauf.id}/${pfad}`); toast.success(ok); laden(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Fehlgeschlagen'); }
    setBusy('');
  };

  const v = d?.voraussetzungen;
  return (
    <div className="space-y-4 pt-6 border-t border-border/60" data-testid="einzug">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-extrabold flex items-center gap-2"><Landmark className="w-5 h-5" />SEPA-Einzug</h2>
          <p className="text-xs text-muted-foreground max-w-2xl">Aus den festgeschriebenen Rechnungen (bis einschließlich {monat}) entsteht die Lastschriftdatei wie bisher bei Sticky. Datei herunterladen, im Sparkassen-Onlinebanking hochladen, dann hier „Bei der Bank eingereicht“ bestätigen.
            {v?.glaeubiger_id && <> Gläubiger-ID {v.glaeubiger_id}{v.iban_maskiert ? ` · Konto ${v.iban_maskiert}` : ''}.</>}</p>
        </div>
        {!offen && (
          <div className="flex items-end gap-2">
            <label className="text-xs text-muted-foreground">Einzugstag<br />
              <input type="date" value={tag} min={d?.fruehestens} onChange={e => setTag(e.target.value)} className="flex h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid="einzug-tag" />
            </label>
            <label className="flex items-center gap-1.5 text-xs cursor-pointer pb-2" title="Übergangsmonat: Mandate aus dem Sticky-Import bleiben draußen, die zieht Sticky selbst ein">
              <input type="checkbox" className="accent-marke" checked={nurCrm} onChange={e => { setNurCrm(e.target.checked); setVorschau(null); }} data-testid="einzug-nur-crm" /> nur neue CRM-Mandate (Sticky zieht den Rest)</label>
            <Button size="sm" variant="outline" disabled={!!busy || !v?.ok} onClick={vorschauLaden}>{busy === 'vorschau' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Vorschau'}</Button>
            <Button size="sm" className="bg-marke hover:bg-marke-dunkel" disabled={!!busy || !v?.ok} onClick={() => setFrage({ art: 'anlegen' })} data-testid="einzug-anlegen">Einzugslauf anlegen</Button>
          </div>
        )}
      </div>
      <EinzugAutomatik />
      {!offen && <BezahltPruefen monat={monat} onVermerkt={() => setVorschau(null)} />}
      {v && !v.ok && <p className="text-sm text-red-600 flex items-center gap-2"><AlertTriangle className="w-4 h-4" />Firmenkonto unvollständig — in Coolify fehlt: {v.fehlt.join(', ')}</p>}
      {v && !v.feld_schluessel && <p className="text-xs text-amber-700 flex items-center gap-2"><AlertTriangle className="w-3 h-3" />FELD_SCHLUESSEL ist nicht gesetzt — die IBANs sind mit einem aus JWT_SECRET abgeleiteten Schlüssel verschlüsselt. Eigenen Schlüssel in Coolify eintragen (Anleitung im Compose), neu starten, dann hier „umschlüsseln“.</p>}
      {v?.feld_schluessel && schluessel && schluessel.alt > 0 && (
        <p className="text-xs text-amber-700 flex flex-wrap items-center gap-2" data-testid="schluessel-alt"><AlertTriangle className="w-3 h-3" />
          {schluessel.alt} Felder (IBANs/Unterschriften) hängen noch am alten, aus JWT_SECRET abgeleiteten Schlüssel — lesbar, aber bei einer JWT-Rotation verloren.
          <Button size="sm" variant="outline" className="h-7" disabled={!!busy} onClick={umschluesseln}>{busy === 'schluessel' ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Jetzt auf FELD_SCHLUESSEL umschlüsseln'}</Button></p>
      )}
      {v?.feld_schluessel && schluessel && schluessel.alt === 0 && schluessel.unlesbar > 0 && <p className="text-xs text-red-600">{schluessel.unlesbar} verschlüsselte Felder sind mit keinem bekannten Schlüssel lesbar — FELD_SCHLUESSEL_ALT prüfen.</p>}

      {vorschau && !offen && (
        <Card><CardContent className="p-4 space-y-2 text-sm">
          <div className="flex flex-wrap gap-4"><span><b>{vorschau.anzahl}</b> Lastschriften</span><span><b>{euro(vorschau.summe)}</b></span><span>Einzug am <b>{datum(vorschau.einzugstag)}</b></span>
            {vorschau.nicht_im_lauf.length > 0 && <span className="text-amber-700">{vorschau.nicht_im_lauf.length} nicht im Lauf</span>}</div>
          {vorschau.anzahl === 0 && <p className="text-xs text-muted-foreground">Nichts einzuziehen: Es zählen nur Rechnungen, die oben <b>freigegeben &amp; festgeschrieben</b> sind und ein gültiges Mandat haben.</p>}
          <Positionen lauf={vorschau} alle={offenAlle.vorschau} toggle={() => setOffenAlle(o => ({ ...o, vorschau: !o.vorschau }))} />
        </CardContent></Card>
      )}

      {(d?.laeufe || []).map(lauf => {
        const [lab, cls] = STATUS[lauf.status] || [lauf.status, ''];
        return (
          <Card key={lauf.id} data-testid={`einzug-lauf-${lauf.status}`}><CardContent className="p-4 space-y-3 text-sm">
            <div className="flex flex-wrap items-center gap-3">
              <span className={`rounded-full px-2 py-0.5 text-xs ${cls}`}>{lab}</span>
              <span><b>{lauf.anzahl}</b> Lastschriften · <b>{euro(lauf.summe)}</b> · Einzug am <b>{datum(lauf.einzugstag)}</b>{lauf.nur_crm_mandate && <span className="text-muted-foreground"> · nur CRM-Mandate</span>}</span>
              {lauf.msg_id && <span className="text-xs text-muted-foreground">Datei {lauf.msg_id}</span>}
              {lauf.eingereicht_am && <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-600" />eingereicht {new Date(lauf.eingereicht_am).toLocaleString('de-DE')} von {lauf.eingereicht_von}</span>}
              <span className="flex-1" />
              {(lauf.status === 'entwurf' || lauf.status === 'datei_erzeugt') && <>
                <Button size="sm" variant="outline" disabled={!!busy || !lauf.anzahl} onClick={() => herunterladen(lauf)} className="gap-1"><Download className="w-4 h-4" />Datei herunterladen</Button>
                <Button size="sm" className="bg-marke hover:bg-marke-dunkel" disabled={!!busy || lauf.status !== 'datei_erzeugt'} onClick={() => setFrage({ art: 'einreichen', lauf })} data-testid="einzug-einreichen">Bei der Bank eingereicht</Button>
                <button className="text-xs underline text-muted-foreground" disabled={!!busy} onClick={() => setFrage({ art: 'verwerfen', lauf })}>verwerfen</button>
              </>}
              {lauf.status === 'eingereicht' && <Button size="sm" variant="outline" disabled={!!busy} onClick={() => herunterladen(lauf)} className="gap-1"><Download className="w-4 h-4" />Datei erneut</Button>}
            </div>
            <Positionen lauf={lauf} alle={offenAlle[lauf.id]} toggle={() => setOffenAlle(o => ({ ...o, [lauf.id]: !o[lauf.id] }))} />
          </CardContent></Card>
        );
      })}

      <Dialog open={!!frage} onOpenChange={(o) => !o && setFrage(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>
            {frage?.art === 'anlegen' && 'Einzugslauf anlegen?'}{frage?.art === 'einreichen' && 'Bei der Bank eingereicht?'}{frage?.art === 'verwerfen' && 'Lauf verwerfen?'}
          </DialogTitle></DialogHeader>
          {frage?.art === 'anlegen' && <p className="text-sm">Alle festgeschriebenen Rechnungen bis {monat} mit gültigem Mandat werden für den Einzug am <b>{datum(tag)}</b> reserviert. Rechnungen ohne Mandat oder mit Zahlweise bar/Überweisung bleiben draußen (mit Grund sichtbar). Bis zum Einreichen lässt sich der Lauf verwerfen.</p>}
          {frage?.art === 'einreichen' && <p className="text-sm">Nur bestätigen, wenn die Datei <b>{frage.lauf.msg_id}</b> ({frage.lauf.anzahl} Lastschriften, {euro(frage.lauf.summe)}) im Onlinebanking hochgeladen und freigegeben ist. Danach gelten die Mandate als genutzt und die Rechnungen als eingezogen — nicht umkehrbar.</p>}
          {frage?.art === 'verwerfen' && <p className="text-sm">Die {frage.lauf.anzahl} Rechnungen werden wieder frei und kommen in den nächsten Lauf. Eine schon hochgeladene Datei musst du bei der Bank selbst zurückziehen.</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setFrage(null)}>Abbrechen</Button>
            {frage?.art === 'anlegen' && <Button className="bg-marke hover:bg-marke-dunkel" onClick={anlegen} data-testid="einzug-bestaetigen">Anlegen</Button>}
            {frage?.art === 'einreichen' && <Button className="bg-marke hover:bg-marke-dunkel" onClick={() => aktion(frage.lauf, 'einreichen', 'Lauf eingereicht — Mandate und Rechnungen vermerkt')}>Ja, eingereicht</Button>}
            {frage?.art === 'verwerfen' && <Button variant="destructive" onClick={() => aktion(frage.lauf, 'verwerfen', 'Lauf verworfen')}>Verwerfen</Button>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Positionen({ lauf, alle, toggle }) {
  const pos = lauf.positionen || [];
  const nicht = lauf.nicht_im_lauf || [];
  const zeigen = alle ? pos : pos.slice(0, 8);
  return (
    <div className="space-y-2">
      {pos.length > 0 && (
        <table className="w-full text-xs">
          <thead className="text-muted-foreground"><tr><th className="text-left py-1 font-semibold">Zahler</th><th className="text-left py-1 font-semibold">Rechnung</th><th className="text-left py-1 font-semibold">Mandat</th><th className="text-left py-1 font-semibold">Konto</th><th className="text-right py-1 font-semibold">Betrag</th></tr></thead>
          <tbody>{zeigen.map(p => (
            <tr key={p.rv_id} className="border-t border-border/40"><td className="py-1">{p.kontoinhaber || p.zahler_name}{p.kontoinhaber && p.kontoinhaber !== p.zahler_name ? <span className="text-muted-foreground"> für {p.zahler_name}</span> : ''}</td>
              <td className="py-1">{p.rechnungsnummer}<span className="text-muted-foreground"> · {p.monat}</span></td><td className="py-1">{p.mandatsreferenz} <span className="text-muted-foreground">{p.sequenz}</span></td>
              <td className="py-1 tabular-nums">{p.iban_maskiert}</td><td className="py-1 text-right tabular-nums">{euro(p.betrag)}</td></tr>
          ))}</tbody>
        </table>
      )}
      {pos.length > 8 && <button className="text-xs underline" onClick={toggle}>{alle ? 'weniger zeigen' : `alle ${pos.length} zeigen`}</button>}
      {nicht.length > 0 && (
        <details className="text-xs"><summary className="cursor-pointer text-amber-700">{nicht.length} nicht im Lauf</summary>
          <ul className="mt-1 space-y-0.5">{nicht.map(n => <li key={n.rv_id} className="flex gap-2"><span className="font-medium">{n.zahler_name}</span><span className="text-muted-foreground">{n.rechnungsnummer || ''} {n.betrag != null ? euro(n.betrag) : ''} — {n.grund}</span></li>)}</ul>
        </details>
      )}
    </div>
  );
}

/* Automatik (11.10.2026): vor dem 1. und 15. bereitet das CRM Rechnungsvorschlag und Einzugslauf selbst vor und
   meldet sich. Es schreibt nichts fest und schickt nichts zur Bank. */
function EinzugAutomatik() {
  const [a, setA] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get('/einzug/automatik').then(r => setA(r.data)).catch(() => setA(null)); }, []);
  if (!a) return null;
  const setzen = async (aenderung) => {
    setBusy(true);
    try { setA((await api.put('/einzug/automatik', aenderung)).data); toast.success('Gespeichert'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Nicht gespeichert'); }
    setBusy(false);
  };
  const e = a.einstellung, p = a.plan;
  return (
    <div className="rounded-lg border border-border/60 px-3 py-2 text-xs space-y-1.5" data-testid="einzug-automatik">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <label className="flex items-center gap-1.5 font-semibold cursor-pointer">
          <input type="checkbox" className="accent-marke" disabled={busy} checked={!!e.aktiv} onChange={ev => setzen({ aktiv: ev.target.checked })} data-testid="einzug-automatik-aktiv" />
          Einzug zum 1. und 15. automatisch vorbereiten
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer" title="Solange Sticky noch selbst einzieht: Mandate aus dem Sticky-Import bleiben draußen">
          <input type="checkbox" className="accent-marke" disabled={busy || !e.aktiv} checked={!!e.nur_crm_mandate} onChange={ev => setzen({ nur_crm_mandate: ev.target.checked })} />
          nur neue CRM-Mandate
        </label>
      </div>
      <p className="text-muted-foreground">
        Nächster Einzug: <b className="text-foreground">{datum(p.einzugstag)}</b>. Das CRM bereitet ab {datum(p.vorbereiten_ab)} vor{p.mit_rechnungsvorschlag ? ' (mit Rechnungsvorschlag)' : ''}.
        Die Datei muss bis <b className="text-foreground">{datum(p.abgabe_bis)}</b> bei der Sparkasse sein. Freigeben, Hochladen und „eingereicht“ bleiben bei dir.
      </p>
    </div>
  );
}

